/**
 * Package Hook
 *
 * React Query hook for fetching and working with package/plan limits
 *
 * **Team-Scoped Behavior:**
 * - Package data is team-scoped and requires a valid teamId
 * - Regular users: Uses their home team's package (user.teamId)
 * - Agency staff in God Mode: Uses client's package (user.currentTeamId)
 * - Agency staff without home team: agency package (key ['no-team']) until they enter God Mode
 *
 * **Query Behavior:**
 * - Query is enabled for users with an effective team, and for agency staff without one
 *   (they receive the agency Plan; the response omits per-brand usage in that context)
 * - Query is disabled for everyone else who has no effective team
 * - Once agency staff enter a client workspace (God Mode), the query switches to that brand's Plan
 *
 * **Example:**
 * ```typescript
 * // Regular user with home team
 * const { data, isLoading, hasFeature } = usePackage();
 * // teamId = 42, query runs, package data loaded
 *
 * // Agency staff without home team, not in God Mode
 * const { data, isLoading } = usePackage();
 * // teamId = null, query runs with key ['no-team'], data = agency Plan (no per-brand usage)
 *
 * // Agency staff in God Mode (viewing client workspace)
 * const { data, isLoading } = usePackage();
 * // teamId = 123 (client's team), query runs, client's package data loaded
 * ```
 */

import { useQuery } from '@tanstack/react-query';
import { packageService } from '@/services/package.service';
import { TeamPackageResponse } from '@/types/package';
import { teamQueryKeys } from '@/lib/queryKeys';
import { useEffectiveTeamId } from '@/hooks/useEffectiveTeamId';

/**
 * Feature name to usage/limit mapping
 */
const FEATURE_MAPPINGS: Record<string, {
  usageKey?: Exclude<keyof TeamPackageResponse['usage'], 'brandSpaces' | 'storagePool' | 'portfolioStoragePool'>;
  limitKey?: keyof TeamPackageResponse['package'];
  booleanKey?: keyof TeamPackageResponse['package'];
}> = {
  'max_channels': { usageKey: 'channels', limitKey: 'maxChannels' },
  'max_users': { usageKey: 'users', limitKey: 'maxUsers' },
  'max_calendars': { usageKey: 'calendars', limitKey: 'maxCalendars' },
  'max_ai_tokens': { usageKey: 'aiTokensUsed', limitKey: 'maxAITokens' },
  'max_storage': { usageKey: 'storageMb', limitKey: 'maxStorage' },
  'max_variations': { limitKey: 'maxVariations' },
  'max_workspaces': { limitKey: 'maxWorkspaces' },
  'posts': { usageKey: 'posts' },
  'whitelabel_enabled': { booleanKey: 'whitelabelEnabled' },
  'canva_enabled': { booleanKey: 'canvaEnabled' },
  'gdrive_enabled': { booleanKey: 'gdriveEnabled' },
  'brand_check_enabled': { booleanKey: 'brandCheckEnabled' },
  'analytics_enabled': { booleanKey: 'analyticsEnabled' },
};

/**
 * Hook for accessing package/plan data and checking limits
 *
 * @param enabled - Whether to enable the query (default: true)
 *                  Pass false to prevent the query from running (e.g., during logout)
 */
export const usePackage = (enabled: boolean = true) => {
  const { teamId, hasTeam, isAgencyStaff } = useEffectiveTeamId();

  const query = useQuery({
    queryKey: teamQueryKeys.package.team(teamId),
    queryFn: packageService.getTeamPackage,
    staleTime: 30 * 1000, // 30 seconds - balance between performance and freshness during team switching
    enabled: (hasTeam || isAgencyStaff) && (enabled ?? true), // Team members, or team-less agency staff (backend resolves their agency package)
  });

  const { data, isLoading, error } = query;

  /**
   * Check if a boolean feature is enabled
   */
  const hasFeature = (featureName: string): boolean => {
    if (!data) return false;

    // Row-backed legacy key: read the resolved (mode-aware) entitlements, not the raw package_features row.
    // The resolver mirrors the old row semantics, so mode 'off' behaves exactly as before. Falls back to the
    // raw row only when the payload is missing (resolver failure / older backend).
    // Zero-change safety: if the resolver omitted the key, fall through to the raw row logic below.
    if (featureName === 'wordpress' && data.entitlements && 'wordpress' in data.entitlements.features) {
      return data.entitlements.features.wordpress === true;
    }

    const mapping = FEATURE_MAPPINGS[featureName];
    if (mapping?.booleanKey) {
      return data.package[mapping.booleanKey] as boolean;
    }

    // Check in dynamic features array
    const feature = data.package.features.find(f => f.featureName === featureName);
    if (feature && feature.featureType === 'BOOLEAN') {
      return feature.featureValue === 'true';
    }

    return false;
  };

  /**
   * Get limit value for a feature
   * Returns null for unlimited, number for limited, 0 for no data
   */
  const getLimit = (featureName: string): number | null => {
    if (!data) return 0;

    const mapping = FEATURE_MAPPINGS[featureName];
    if (mapping?.limitKey) {
      const limit = data.package[mapping.limitKey];
      // Return null for unlimited (explicit null from API)
      if (limit === null || limit === undefined) return null;
      return typeof limit === 'number' ? limit : 0;
    }

    // Check in dynamic features array
    const feature = data.package.features.find(f => f.featureName === featureName);
    if (feature && feature.featureType === 'INTEGER') {
      return parseInt(feature.featureValue, 10) || 0;
    }

    return 0;
  };

  /**
   * Get current usage for a feature
   */
  const getUsage = (featureName: string): number => {
    if (!data) return 0;

    const mapping = FEATURE_MAPPINGS[featureName];
    if (mapping?.usageKey) {
      return data.usage[mapping.usageKey] || 0;
    }

    return 0;
  };

  /**
   * Check if current usage is within limit
   */
  const isWithinLimit = (featureName: string): boolean => {
    if (!data) return false;

    const limit = getLimit(featureName);
    const usage = getUsage(featureName);

    // null = unlimited, always within limit
    if (limit === null) return true;

    // Unlimited (0 typically means unlimited for non-nullable fields)
    if (limit === 0) return true;

    return usage < limit;
  };

  /**
   * Semantic alias for isWithinLimit - check if we can add one more
   */
  const canAdd = (featureName: string): boolean => {
    return isWithinLimit(featureName);
  };

  /**
   * Get remaining quota for a feature
   */
  const getRemainingQuota = (featureName: string): number => {
    if (!data) return 0;

    const limit = getLimit(featureName);
    const usage = getUsage(featureName);

    // Unlimited (null or 0)
    if (limit === null || limit === 0) return Infinity;

    const remaining = limit - usage;
    return remaining > 0 ? remaining : 0;
  };

  return {
    // Query state
    data,
    isLoading,
    error,
    isExpired: data?.isExpired || false,
    package: data?.package,
    usage: data?.usage,
    /** Brand inside a Brand Portfolio: its slice of the shared storage pool (undefined = standalone brand). */
    storagePool: data?.usage.storagePool,
    entitlements: data?.entitlements ?? null,
    expiresAt: data?.expiresAt,

    // Helper functions
    hasFeature,
    getLimit,
    getUsage,
    isWithinLimit,
    canAdd,
    getRemainingQuota,
  };
};
